/**
 * The Account panel: email → 6-digit code sign-in, session display, sign-out.
 *
 * Wired from main.js (not app.js) on purpose: sketching has zero dependency
 * on auth, and keeping this module out of app.js keeps that boundary
 * mechanical — app.js cannot gate anything on a session it never sees. When
 * the sync engine lands it will consume the same `auth` object; this panel
 * only establishes and displays the session.
 *
 * @param {Document} root
 * @param {ReturnType<import('../sync/auth.js').createAuth>} auth
 */
export function wireAccountPanel(root, auth) {
  const el = (id) => root.getElementById(id);
  const panel = el('account-panel');
  const openBtn = el('open-account');
  if (!panel || !openBtn) return;

  const message = el('account-message');
  const emailForm = el('account-email-form');
  const codeForm = el('account-code-form');

  function say(text, isError = false) {
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('account-message--error', isError);
  }

  /** Swap the card between its signed-in and signed-out faces. */
  function showUser(user) {
    el('account-signed-out').hidden = Boolean(user);
    el('account-signed-in').hidden = !user;
    if (user) el('account-user').textContent = user.email ?? user.id;
    // The app-bar button doubles as the status indicator.
    openBtn.textContent = user ? (user.email ?? 'Account') : 'Sign in';
  }

  openBtn.addEventListener('click', () => {
    panel.hidden = false;
    el(el('account-signed-out').hidden ? 'account-signout' : 'account-email').focus();
  });
  el('close-account')?.addEventListener('click', () => {
    panel.hidden = true;
  });

  emailForm?.addEventListener('submit', async (evt) => {
    evt.preventDefault();
    say('Sending…');
    const result = await auth.requestCode(el('account-email').value);
    if (!result.ok) {
      say(result.message, true);
      return;
    }
    codeForm.hidden = false;
    say('Check that inbox for a 6-digit code (forwarded emails work too — type the code here).');
    el('account-code').focus();
  });

  codeForm?.addEventListener('submit', async (evt) => {
    evt.preventDefault();
    say('Checking…');
    const result = await auth.verifyCode(el('account-email').value, el('account-code').value);
    if (!result.ok) {
      say(result.message, true);
      return;
    }
    say('');
    el('account-code').value = '';
    codeForm.hidden = true;
    // Verified — the dialog's job is done, so it dismisses itself (owner,
    // 2026-08-29); the signed-in state is already on the app-bar chip. The
    // close lives HERE, in the verify-success branch, and deliberately NOT
    // in showUser/onChange: supabase fires that for token refreshes and
    // cross-tab events too, and Sign out lives in this dialog — an
    // already-signed-in designer who opened it on purpose must not have it
    // slam shut mid-read. Sync errors are unaffected either way: they render
    // on the app-bar indicator, never in this panel.
    panel.hidden = true;
  });

  el('account-signout')?.addEventListener('click', async () => {
    await auth.signOut();
  });

  // Paint the initial state from the stored session (no network — works
  // offline), then follow every change.
  auth.currentUser().then(showUser);
  auth.onChange(showUser);
}
